import {
  objectToCamelCase,
  objectToSnakeCase,
  formatDateShort,
  formatNumberAsUSD,
} from '@/services/utils'
import Event from '@/services/events'
import Expense from '@/services/expenses/'
import Employee from '@/services/employees'
import EventCategories from '@/services/eventCategories'
import Organization from '@/services/organizations'
import Staff from '@/services/staff'

import api from './api'
import options from './options'

class RequestQuestion {
  static api = api.create(RequestQuestion)
  constructor({
    id = '',
    text = '',
    qType = '',
    friendlyName = '',
    isOptional = null,
    answer = null,
    answerOptions = [],
    order = '',
    inputMask = '',
  } = {}) {
    Object.assign(this, {
      id,
      text,
      qType,
      friendlyName,
      isOptional,
      answer: RequestQuestionAnswer.fromAPI(answer),
      answerOptions,
      order,
      inputMask,
    })
  }

  static fromAPI(json) {
    return new RequestQuestion(objectToCamelCase(json))
  }
}

class RequestQuestionAnswer {
  constructor(answer) {
    const { text = '' } = answer
    Object.assign(this, {
      text,
    })
  }

  static create(opts) {
    return new RequestQuestionAnswer(opts)
  }

  static fromAPI(json) {
    if (json === null) {
      json = {}
    }
    return new RequestQuestionAnswer(objectToCamelCase(json))
  }
}

export default class StipendRequest {
  static api = api.create(StipendRequest)
  static options = options
  static readOnlyFields = [
    'id',
    'employeeRef',
    'employee',
    'organization',
    'amount',
    'hasUnreadMessages',
    'datetimeCreated',
    'lastEdited',
    'eventsRef',
    'expensesRef',
    'paypalEmail',
    'programMonth',
    'lastUserMessagedAt',
    'lastAdminMessagedAt',
  ]

  static dateToUtcFormat(date) {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
      ),
    )
  }

  constructor(json) {
    Object.assign(this, {
      ...json,
      followUpDate:
        json?.followUpDate && json?.followUpDate != 'undefined'
            ? StipendRequest.dateToUtcFormat(new Date(json.followUpDate))
            : null,
      lastAdminMessagedAt:
        json?.lastAdminMessagedAt && json.lastAdminMessagedAt != 'undefined'
          ? new Date(json.lastAdminMessagedAt)
          : null,
      lastUserMessagedAt:
        json?.lastUserMessagedAt && json.lastUserMessagedAt != 'undefined'
          ? new Date(json.lastUserMessagedAt)
          : null,
    })
  }

  get isClosed() {
    return options.CLOSED_REQUEST_STATUSES.includes(this.status)
  }

  get submittedOnDisplay() {
    return formatDateShort(this.submittedOn)
  }

  get amountDisplay() {
    return formatNumberAsUSD(this.amount)
  }

  get approvedFundsDisplay() {
    return formatNumberAsUSD(this.approvedFunds)
  }

  get submittedDisplay() {
    return this.submittedStatus ? this.submittedStatus.split('_').join(' ') : 'SUBMITTED'
  }

  static create(opts) {
    return new StipendRequest(opts)
  }

  static fromAPI(json) {
    return new StipendRequest(objectToCamelCase(json))
  }

  /**
   * Prep data for the API. Can elect to include or exlude specific fields.
   * The readOnlyFields are always excluded.
   **/
  static toAPI(request = null, fields = [], excludeFields = [], kwargs = null) {
    let data = {}

    if (kwargs) {
      delete kwargs.id
      data = kwargs
    } else {
      // By default, send the whole object
      // If it's a partial update, get only the fields specified

      if (fields.length > 0) {
        fields.forEach(field => {
          data[field] = request[field]
        })
      } else {
        data = request
      }

      //
      excludeFields = [...StipendRequest.readOnlyFields, ...excludeFields]
      excludeFields.forEach(item => {
        delete data[item]
      })
    }

    return objectToSnakeCase(data)
  }

  static checkIsInstance(obj, name) {
    if (!(obj instanceof StipendRequest)) {
      throw Error(`${name} must be an instance of the Stipend Request class.`)
    }
  }
}
